<template>
  <div class="ouvidoria-report">
    <!-- Novo layout com filtros na direita e relatório na esquerda -->
    <b-row class="h-100 no-gutters">
      <!-- Coluna da esquerda - Relatório -->
      <b-col cols="9" class="h-100">
        <b-card v-if="isMasterUser" id="report" no-body class="h-100">
          <div class="report-print-root" :class="printRootClass">
            <ReportPrintHeader
              v-if="printing"
              :title="printTitle"
              :subtitle="`Relatório de ouvidoria · Total: ${totalFilteredCount}`"
              :logo="printLogo"
              :generated-at="formatPrintDateTime(printGeneratedAt)"
              :company="printCompany"
              :period="printPeriod"
              :emitter="printEmitter"
            />
            <b-card-body class="position-relative">
              <div
                class="d-flex justify-content-between align-items-center mb-3 report-screen-heading"
              >
                <h2>{{ reportTitle }}</h2>
                <b-badge variant="info" pill class="p-2">
                  <i class="fas fa-file-alt mr-1"></i>
                  Total: {{ totalFilteredCount }}
                </b-badge>
              </div>

              <div class="complaints-layout-surface">
                <div v-if="loadingReport" class="skeleton-overlay">
                  <div class="table-responsive">
                    <table class="table complaints-layout-table skeleton-table">
                      <thead class="complaints-layout-header">
                        <tr>
                          <th v-for="field in activeFields" :key="field.key">
                            {{ field.label }}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="i in 8" :key="i">
                          <td v-for="field in activeFields" :key="field.key">
                            <b-skeleton
                              :width="
                                field.key === 'protocol' ? '120px' : '90%'
                              "
                            ></b-skeleton>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div
                  v-if="!loadingReport && reportItems.length === 0"
                  class="complaints-layout-empty text-center"
                >
                  <p>Nenhum item de ouvidoria encontrado.</p>
                </div>

                <b-table
                  v-show="!loadingReport && reportItems.length > 0"
                  borderless
                  hover
                  :responsive="!printing"
                  :items="reportItems"
                  :fields="printFields"
                  :filter="currentFilter"
                  :filter-function="filterResults"
                  table-class="complaints-layout-table"
                  thead-class="complaints-layout-header"
                  :tbody-tr-class="getReportRowClass"
                  @filtered="onFiltered"
                >
                  <template #cell(protocol)="data">
                    <b-button
                      v-if="!printing"
                      size="sm"
                      variant="link"
                      class="p-0 complaints-layout-link"
                      target="_blank"
                      :to="`/ouvidoria/${data.item._id}`"
                    >
                      {{ data.value }}
                      <i class="fas fa-external-link-alt" aria-hidden="true" />
                    </b-button>
                    <span v-else class="complaints-layout-link">{{
                      data.value
                    }}</span>
                  </template>
                  <template #cell(status)="data">
                    <b-badge :variant="getStatusBadgeVariant(data.value)">
                      {{ getStatusText(data.value) }}
                    </b-badge>
                  </template>
                  <template #cell(time)="data">
                    {{ duration(data.value) }}
                  </template>
                  <template #cell(companyName)="data">
                    {{ data.value }}
                  </template>
                </b-table>
              </div>
            </b-card-body>
          </div>
        </b-card>

        <b-card v-else id="report" class="h-100">
          <div class="report-print-root" :class="printRootClass">
            <ReportPrintHeader
              v-if="printing"
              :title="printTitle"
              :subtitle="`Relatório de ouvidoria · Total: ${totalFilteredCount}`"
              :logo="printLogo"
              :generated-at="formatPrintDateTime(printGeneratedAt)"
              :company="printCompany"
              :period="printPeriod"
              :emitter="printEmitter"
            />
            <div
              class="d-flex justify-content-between align-items-center mb-3 report-screen-heading"
            >
              <h2>{{ reportTitle }}</h2>
              <b-badge variant="info" pill class="p-2">
                <i class="fas fa-file-alt mr-1"></i>
                Total: {{ totalFilteredCount }}
              </b-badge>
            </div>
            <b-card-body class="position-relative">
              <div class="complaints-layout-surface">
                <div v-if="loadingReport" class="skeleton-overlay">
                  <div class="table-responsive">
                    <table class="table complaints-layout-table skeleton-table">
                      <thead class="complaints-layout-header">
                        <tr>
                          <th v-for="field in activeFields" :key="field.key">
                            {{ field.label }}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="i in 8" :key="i">
                          <td v-for="field in activeFields" :key="field.key">
                            <b-skeleton
                              :width="
                                field.key === 'protocol' ? '120px' : '90%'
                              "
                            ></b-skeleton>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div
                  v-if="!loadingReport && reportItems.length === 0"
                  class="complaints-layout-empty text-center"
                >
                  <p>Nenhum item de ouvidoria encontrado.</p>
                </div>

                <b-table
                  v-show="!loadingReport && reportItems.length > 0"
                  borderless
                  hover
                  :responsive="!printing"
                  :filter="currentFilter"
                  :filter-function="filterResults"
                  :items="reportItems"
                  :fields="printFields"
                  class="force-printing-overflow"
                  table-class="complaints-layout-table"
                  thead-class="complaints-layout-header"
                  :tbody-tr-class="getReportRowClass"
                  @filtered="onFiltered"
                >
                  <template #cell(protocol)="data">
                    <b-button
                      v-if="!printing"
                      size="sm"
                      variant="link"
                      class="p-0 complaints-layout-link"
                      target="_blank"
                      :to="`/ouvidoria/${data.item._id}`"
                    >
                      {{ data.value }}
                      <i class="fas fa-external-link-alt" aria-hidden="true" />
                    </b-button>
                    <span v-else class="complaints-layout-link">{{
                      data.value
                    }}</span>
                  </template>
                  <template #cell(status)="data">
                    <b-badge :variant="getStatusBadgeVariant(data.value)">
                      {{ getStatusText(data.value) }}
                    </b-badge>
                  </template>
                  <template #cell(time)="data">
                    {{ duration(data.value) }}
                  </template>
                  <template #cell(companyName)="data">
                    {{ data.value }}
                  </template>
                </b-table>
              </div>
            </b-card-body>
          </div>
        </b-card>
      </b-col>
      <!-- Coluna da direita - Filtros -->
      <b-col cols="3" class="pl-3">
        <b-card header="Parâmetros" class="filters-sidebar h-100">
          <div class="filters-container">
            <!-- Filtro de Empresa (apenas para root ou company-root gestora) -->
            <div v-if="showCompanyFilter" class="filter-group">
              <div class="filter-header" @click="toggleSection('empresa')">
                <h6 class="filter-title">Empresa</h6>
                <b-button variant="link" size="sm" class="collapse-btn p-0">
                  <i
                    :class="
                      collapsedSections.empresa
                        ? 'fas fa-chevron-down'
                        : 'fas fa-chevron-up'
                    "
                  />
                </b-button>
              </div>
              <b-collapse
                id="collapse-empresa"
                :visible="!collapsedSections.empresa"
              >
                <b-form-group label="Selecionar Empresa" class="mb-3">
                  <b-form-select
                    v-model="filter.selectedCompany"
                    :options="companyOptions"
                    class="mb-2"
                    @change="onFilterChange"
                  >
                    <template #first>
                      <b-form-select-option :value="null"
                        >Todas as Empresas</b-form-select-option
                      >
                    </template>
                  </b-form-select>
                </b-form-group>
              </b-collapse>
            </div>

            <!-- Filtros básicos -->
            <div class="filter-group">
              <div class="filter-header" @click="toggleSection('periodo')">
                <h6 class="filter-title">Período</h6>
                <b-button variant="link" size="sm" class="collapse-btn p-0">
                  <i
                    :class="
                      collapsedSections.periodo
                        ? 'fas fa-chevron-down'
                        : 'fas fa-chevron-up'
                    "
                  />
                </b-button>
              </div>
              <b-collapse
                id="collapse-periodo"
                :visible="!collapsedSections.periodo"
              >
                <b-form-group label="Data inicial" class="mb-2">
                  <b-form-input
                    v-model="filter.fromDate"
                    type="date"
                    @change="onFilterChange"
                  />
                </b-form-group>
                <b-form-group label="Data final" class="mb-3">
                  <b-form-input
                    v-model="filter.toDate"
                    type="date"
                    @change="onFilterChange"
                  />
                </b-form-group>
              </b-collapse>
            </div>

            <!-- Filtro de Status -->
            <div class="filter-group">
              <div class="filter-header" @click="toggleSection('situacao')">
                <h6 class="filter-title">Situação</h6>
                <b-button variant="link" size="sm" class="collapse-btn p-0">
                  <i
                    :class="
                      collapsedSections.situacao
                        ? 'fas fa-chevron-down'
                        : 'fas fa-chevron-up'
                    "
                  />
                </b-button>
              </div>
              <b-collapse
                id="collapse-situacao"
                :visible="!collapsedSections.situacao"
              >
                <b-form-checkbox-group
                  v-model="filter.status"
                  :options="statusOpts"
                  stacked
                  class="mb-3"
                  @change="onFilterChange"
                />
              </b-collapse>
            </div>

            <!-- Filtros de Classificação -->
            <div v-if="!isMasterUser" class="filter-group">
              <div
                class="filter-header"
                @click="toggleSection('classificacoes')"
              >
                <h6 class="filter-title">Classificações</h6>
                <b-button variant="link" size="sm" class="collapse-btn p-0">
                  <i
                    :class="
                      collapsedSections.classificacoes
                        ? 'fas fa-chevron-down'
                        : 'fas fa-chevron-up'
                    "
                  />
                </b-button>
              </div>
              <b-collapse
                id="collapse-classificacoes"
                :visible="!collapsedSections.classificacoes"
              >
                <b-form-group
                  v-if="
                    companyClassification.receivingMeans &&
                    companyClassification.receivingMeans.enabled
                  "
                  label="Meio de Recebimento"
                  class="mb-2"
                >
                  <b-form-checkbox-group
                    v-model="filter.receivingMeans"
                    :options="getClassificationOpts('receivingMeans')"
                    stacked
                    @change="onFilterChange"
                  />
                </b-form-group>

                <b-form-group
                  v-if="
                    companyClassification.origin &&
                    companyClassification.origin.enabled
                  "
                  label="Origem"
                  class="mb-2"
                >
                  <b-form-checkbox-group
                    v-model="filter.origin"
                    :options="getClassificationOpts('origin')"
                    stacked
                    @change="onFilterChange"
                  />
                </b-form-group>

                <b-form-group
                  v-if="
                    companyClassification.reportingAgent &&
                    companyClassification.reportingAgent.enabled
                  "
                  label="Agente Denunciante"
                  class="mb-2"
                >
                  <b-form-checkbox-group
                    v-model="filter.reportingAgent"
                    :options="getClassificationOpts('reportingAgent')"
                    stacked
                    @change="onFilterChange"
                  />
                </b-form-group>

                <b-form-group
                  v-if="
                    companyClassification.level &&
                    companyClassification.level.enabled
                  "
                  label="Classificação/Classe Interna"
                  class="mb-2"
                >
                  <b-form-checkbox-group
                    v-model="filter.level"
                    :options="getClassificationOpts('level')"
                    stacked
                    @change="onFilterChange"
                  />
                </b-form-group>

                <b-form-group
                  v-if="
                    companyClassification.nature &&
                    companyClassification.nature.enabled
                  "
                  label="Natureza"
                  class="mb-2"
                >
                  <b-form-checkbox-group
                    v-model="filter.nature"
                    :options="getClassificationOpts('nature')"
                    stacked
                    @change="onFilterChange"
                  />
                </b-form-group>

                <b-form-group
                  v-if="
                    companyClassification.responsibleDepartment &&
                    companyClassification.responsibleDepartment.enabled
                  "
                  label="Setor Responsável"
                  class="mb-2"
                >
                  <b-form-checkbox-group
                    v-model="filter.responsibleDepartment"
                    :options="getClassificationOpts('responsibleDepartment')"
                    stacked
                    @change="onFilterChange"
                  />
                </b-form-group>

                <b-form-group
                  v-if="
                    companyClassification.involvedDepartment &&
                    companyClassification.involvedDepartment.enabled
                  "
                  label="Setor da Ocorrência"
                  class="mb-2"
                >
                  <b-form-checkbox-group
                    v-model="filter.involvedDepartment"
                    :options="getClassificationOpts('involvedDepartment')"
                    stacked
                    @change="onFilterChange"
                  />
                </b-form-group>

                <b-form-group
                  v-if="
                    companyClassification.businessUnit &&
                    companyClassification.businessUnit.enabled
                  "
                  label="Unidade"
                  class="mb-2"
                >
                  <b-form-checkbox-group
                    v-model="filter.businessUnit"
                    :options="getClassificationOpts('businessUnit')"
                    stacked
                    @change="onFilterChange"
                  />
                </b-form-group>

                <b-form-group
                  v-if="
                    companyClassification.businessGroup &&
                    companyClassification.businessGroup.enabled
                  "
                  label="Empresa do Grupo"
                  class="mb-2"
                >
                  <b-form-checkbox-group
                    v-model="filter.businessGroup"
                    :options="getClassificationOpts('businessGroup')"
                    stacked
                    @change="onFilterChange"
                  />
                </b-form-group>

                <b-form-group
                  v-if="
                    companyClassification.relatedProject &&
                    companyClassification.relatedProject.enabled
                  "
                  label="Contrato/Projeto"
                  class="mb-2"
                >
                  <b-form-checkbox-group
                    v-model="filter.relatedProject"
                    :options="getClassificationOpts('relatedProject')"
                    stacked
                    @change="onFilterChange"
                  />
                </b-form-group>
              </b-collapse>
            </div>

            <!-- Campos -->
            <div class="filter-group">
              <div class="filter-header" @click="toggleSection('campos')">
                <h6 class="filter-title">Campos</h6>
                <b-button variant="link" size="sm" class="collapse-btn p-0">
                  <i
                    :class="
                      collapsedSections.campos
                        ? 'fas fa-chevron-down'
                        : 'fas fa-chevron-up'
                    "
                  />
                </b-button>
              </div>
              <b-collapse
                id="collapse-campos"
                :visible="!collapsedSections.campos"
              >
                <b-form-checkbox-group
                  v-model="selectedColumns"
                  :options="availableColumns"
                  name="flavour-1"
                  class="mb-3"
                  @change="updateFields"
                />
              </b-collapse>
            </div>

            <!-- Botões de ação -->
            <div class="d-flex flex-column mt-3">
              <b-button
                variant="secondary"
                class="mb-2"
                @click="clearParameters"
              >
                <i class="fas fa-trash"></i> Limpar
              </b-button>
              <b-button variant="primary" @click="print">
                <i class="fas fa-print"></i> Imprimir
              </b-button>
              <b-button variant="success" class="mt-2" @click="exportCSV">
                <i class="fas fa-file-csv"></i> Exportar CSV
              </b-button>
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import humanizeDuration from "humanize-duration";
import { mapGetters } from "vuex";
import { ombudsmanProcessingTimeReport } from "@/api/reports";
import OmbudsmanStatus from "../../../../../backend/models/ombudsman/OmbudsmanStatus.ts";
import moment from "moment";
import {
  getCompanyClassifications,
  getCompanyUsers,
  getCompanies,
} from "@/api/company";
import ReportPrintHeader from "@/components/ReportPrintHeader.vue";
import reportPrintMixin from "@/mixins/reportPrint";

export default {
  name: "OuvidoriaReport",
  components: {
    ReportPrintHeader,
  },
  mixins: [reportPrintMixin],
  data() {
    const statusOpts = [];
    for (const status of Object.keys(OmbudsmanStatus)) {
      const text = this.getStatusText(OmbudsmanStatus[status]);
      statusOpts.push({ value: OmbudsmanStatus[status], text });
    }

    return {
      totalRows: 0,
      loadingReport: false,
      statusOpts,
      selectedColumns: [],
      availableColumns: [],
      filter: {
        selectedCompany: null,
        fromDate: null,
        toDate: null,
        status: [],
        receivingMeans: [],
        origin: [],
        reportingAgent: [],
        level: [],
        nature: [],
        responsibleDepartment: [],
        involvedDepartment: [],
        businessUnit: [],
        businessGroup: [],
        relatedProject: [],
      },
      companyClassification: {
        receivingMeans: {},
        origin: {},
        reportingAgent: {},
        level: {},
        nature: {},
        responsibleDepartment: {},
        involvedDepartment: {},
        businessUnit: {},
        businessGroup: {},
        relatedProject: {},
      },
      currentFilter: {
        selectedCompany: null,
        status: [],
        toDate: null,
        fromDate: null,
        receivingMeans: [],
        origin: [],
        reportingAgent: [],
        level: [],
        nature: [],
        responsibleDepartment: [],
        involvedDepartment: [],
        businessUnit: [],
        businessGroup: [],
        relatedProject: [],
      },
      report: [],
      hcfg: { language: "pt", largest: 3 },
      fields: [{ key: "protocol", label: "Protocolo", sortable: true }],
      companyUsers: {},
      availableCompanies: [],
      collapsedSections: {
        empresa: false,
        periodo: false,
        situacao: false,
        classificacoes: true,
        campos: true,
      },
    };
  },
  computed: {
    ...mapGetters(["profile", "classificationOptions", "classificationMaps"]),
    isMasterUser() {
      return this.profile.role === "root" || this.isCompanyManager;
    },
    isCompanyManager() {
      return (
        this.profile.role === "company-root" &&
        this.profile.managedCompanies &&
        this.profile.managedCompanies.length > 0
      );
    },
    showCompanyFilter() {
      return this.profile.role === "root" || this.isCompanyManager;
    },
    companyOptions() {
      const options = [];

      if (this.availableCompanies && this.availableCompanies.length > 0) {
        this.availableCompanies.forEach((company) => {
          options.push({
            value: company._id,
            text: company.fantasyName || company.name,
          });
        });
      }

      return options;
    },
    showCompanyColumn() {
      return this.isMasterUser && !this.currentFilter.selectedCompany;
    },
    activeFields() {
      if (!this.showCompanyColumn) {
        return this.fields;
      }

      const [firstField, ...remainingFields] = this.fields;

      return [
        firstField,
        { key: "companyName", label: "Empresa", sortable: true },
        ...remainingFields,
      ];
    },
    filteredReport() {
      if (!this.showCompanyFilter || !this.currentFilter.selectedCompany) {
        return this.report;
      }
      // Verificar se report é um array antes de filtrar
      if (!Array.isArray(this.report)) return this.report;
      return this.report.filter(
        (item) =>
          item.company &&
          item.company._id === this.currentFilter.selectedCompany
      );
    },
    reportItems() {
      if (this.isMasterUser) {
        const groupedReport = Array.isArray(this.filteredReport)
          ? this.filteredReport
          : [];

        return groupedReport.flatMap((item) => {
          const companyName =
            item.company?.fantasyName || item.company?.name || "N/A";

          return (item.ombudsmans || []).map((ombudsman) => ({
            ...ombudsman,
            companyName,
          }));
        });
      }

      return (this.report.ombudsmans || []).map((ombudsman) => ({
        ...ombudsman,
        companyName:
          this.report.company?.fantasyName || this.report.company?.name || "",
      }));
    },
    reportTitle() {
      if (!this.isMasterUser) {
        return `Ouvidoria da ${
          this.report.company ? this.report.company.fantasyName : "Empresa"
        }`;
      }

      if (this.currentFilter.selectedCompany) {
        const selectedCompany = this.companyOptions.find(
          (company) => company.value === this.currentFilter.selectedCompany
        );

        return `Ouvidoria - ${
          selectedCompany ? selectedCompany.text : "Empresa Selecionada"
        }`;
      }

      return "Ouvidoria";
    },
    printTitle() {
      return this.reportTitle;
    },
    totalFilteredCount() {
      return this.reportItems.filter((row) =>
        this.filterResults(row, this.currentFilter)
      ).length;
    },
  },
  async created() {
    await this.loadCompanyPrintLogo(this.profile.company);
    this.loadingReport = true;

    try {
      // Carregar empresas para o filtro (root ou company-root gestora)
      if (this.showCompanyFilter) {
        await this.loadCompanies();
      }

      if (this.isMasterUser || this.isCompanyManager) {
        let response;
        try {
          this.updateColumnsOptions();
          // Chamar sem companyId para receber todas as empresas (root ou company-root gestora)
          response = await ombudsmanProcessingTimeReport(null);
        } catch (error) {
          if (error.response.status === 403) {
            this.$bvToast.toast(error.response.data, {
              title: "Erro",
              toastClass: "custom-toast custom-toast-error",
              variant: "light",
              solid: true,
              autoHideDelay: 10000,
              noCloseButton: false,
            });
          } else {
            this.$bvToast.toast(error.response.statusText, {
              title: "Erro",
              toastClass: "custom-toast custom-toast-error",
              variant: "light",
              solid: true,
              autoHideDelay: 10000,
              noCloseButton: false,
            });
          }
          return;
        }
        this.report = response.data;
      } else {
        await this.$store.dispatch(
          "RefreshCompanyClassifications",
          this.profile.company
        );
        let response;
        try {
          response = await getCompanyClassifications(this.profile.company);
          this.companyClassification = response.data;
          this.updateColumnsOptions();
          response = await getCompanyUsers(this.profile.company, true);
          this.companyUsers = response.data.reduce((obj, user) => {
            obj[user["_id"]] = user;
            return obj;
          }, {});
          response = await ombudsmanProcessingTimeReport(this.profile.company);
        } catch (error) {
          if (error.response.status === 403) {
            this.$bvToast.toast(error.response.data, {
              title: "Erro",
              toastClass: "custom-toast custom-toast-error",
              variant: "light",
              solid: true,
              autoHideDelay: 10000,
              noCloseButton: false,
            });
          } else {
            this.$bvToast.toast(error.response.statusText, {
              title: "Erro",
              toastClass: "custom-toast custom-toast-error",
              variant: "light",
              solid: true,
              autoHideDelay: 10000,
              noCloseButton: false,
            });
          }
          return;
        }
        this.report = response.data;
        this.totalRows = this.report.ombudsmans.length;
      }
    } finally {
      this.loadingReport = false;
    }
  },
  methods: {
    getStatusText(status) {
      switch (parseInt(status)) {
        case OmbudsmanStatus.NEW:
          return "Nova";
        case OmbudsmanStatus.PROCESSING:
          return "Em processamento";
        case OmbudsmanStatus.CONCLUDED:
          return "Concluída";
        default:
          return "Desconhecido";
      }
    },
    getReportRowClass(item) {
      if (!item) {
        return "";
      }

      const baseClass = "complaints-layout-row";
      switch (item.status) {
        case OmbudsmanStatus.NEW:
          return `${baseClass} complaints-layout-row--new`;
        case OmbudsmanStatus.CONCLUDED:
          return `${baseClass} complaints-layout-row--concluded`;
        default:
          return baseClass;
      }
    },
    formatDate(value) {
      if (!value) {
        return "—";
      }
      return moment(value).format("DD/MM/YYYY HH:mm");
    },
    getStatusClass(status) {
      const statusMap = {
        [OmbudsmanStatus.NEW]: "status-new",
        [OmbudsmanStatus.PROCESSING]: "status-in-progress",
        [OmbudsmanStatus.CONCLUDED]: "status-concluded",
      };
      return statusMap[status] || "status-default";
    },
    getStatusBadgeVariant(status) {
      const statusMap = {
        [OmbudsmanStatus.NEW]: "primary",
        [OmbudsmanStatus.PROCESSING]: "warning",
        [OmbudsmanStatus.CONCLUDED]: "success",
      };
      return statusMap[status] || "secondary";
    },
    duration(value) {
      return humanizeDuration(value, this.hcfg);
    },
    getClassificationOpts(key) {
      return [
        { value: null, text: "Sem Classificação" },
        ...this.classificationOptions[key],
      ];
    },
    clearParameters() {
      this.filter.selectedCompany = null;
      this.filter.fromDate = null;
      this.filter.toDate = null;
      this.filter.status = [];
      this.filter.receivingMeans = [];
      this.filter.origin = [];
      this.filter.reportingAgent = [];
      this.filter.level = [];
      this.filter.nature = [];
      this.filter.responsibleDepartment = [];
      this.filter.involvedDepartment = [];
      this.filter.businessUnit = [];
      this.filter.businessGroup = [];
      this.filter.relatedProject = [];

      this.receivingMeans = [];
      this.origin = [];
      this.reportingAgent = [];
      this.level = [];
      this.nature = [];
      this.responsibleDepartment = [];
      this.involvedDepartment = [];
      this.businessUnit = [];
      this.businessGroup = [];
      this.relatedProject = [];
      this.updateColumnsOptions();
      this.currentFilter = {
        selectedCompany: null,
        status: [],
        receivingMeans: [],
        origin: [],
        reportingAgent: [],
        level: [],
        nature: [],
        responsibleDepartment: [],
        involvedDepartment: [],
        businessUnit: [],
        businessGroup: [],
        relatedProject: [],
        toDate: null,
        fromDate: null,
      };
    },
    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length;
    },
    onFilterChange() {
      if (this.filter.fromDate) {
        const dt = new Date(this.filter.fromDate);
        const offset = dt.getTimezoneOffset();
        this.currentFilter.fromDate = moment(dt)
          .add("minute", offset)
          .startOf("day")
          .toDate();
      } else {
        this.currentFilter.fromDate = null;
      }

      if (this.filter.toDate) {
        const dt = new Date(this.filter.toDate);
        const offset = dt.getTimezoneOffset();
        this.currentFilter.toDate = moment(dt)
          .add("minute", offset)
          .endOf("day")
          .toDate();
      } else {
        this.currentFilter.toDate = null;
      }

      this.currentFilter.selectedCompany = this.filter.selectedCompany;
      this.currentFilter.status = this.filter.status;
      this.currentFilter.receivingMeans = this.filter.receivingMeans;
      this.currentFilter.origin = this.filter.origin;
      this.currentFilter.reportingAgent = this.filter.reportingAgent;
      this.currentFilter.level = this.filter.level;
      this.currentFilter.nature = this.filter.nature;
      this.currentFilter.responsibleDepartment =
        this.filter.responsibleDepartment;
      this.currentFilter.involvedDepartment = this.filter.involvedDepartment;
      this.currentFilter.businessUnit = this.filter.businessUnit;
      this.currentFilter.businessGroup = this.filter.businessGroup;
      this.currentFilter.relatedProject = this.filter.relatedProject;
    },
    filterResults(row, filter) {
      if (filter.fromDate || filter.toDate) {
        const created = moment(row.createdAt).toDate();
        if (filter.fromDate && filter.fromDate > created) {
          return false;
        }
        if (filter.toDate && filter.toDate < created) {
          return false;
        }
      }
      if (filter.status.length && filter.status.indexOf(row.status) === -1) {
        return false;
      }
      const classifications = [
        "receivingMeans",
        "origin",
        "reportingAgent",
        "level",
        "nature",
        "responsibleDepartment",
        "involvedDepartment",
        "businessUnit",
        "businessGroup",
        "relatedProject",
      ];
      const rowClassification = row.classification || {};
      for (const key of classifications) {
        if (
          filter[key].length &&
          filter[key].indexOf(rowClassification[key]) === -1
        ) {
          return false;
        }
      }
      return true;
    },

    exportCSV() {
      const classificationKeys = [
        "receivingMeans",
        "origin",
        "reportingAgent",
        "level",
        "nature",
        "responsibleDepartment",
        "involvedDepartment",
        "businessUnit",
        "businessGroup",
        "relatedProject",
      ];

      const escapeCsv = (value) => {
        if (value === null || value === undefined) return "";
        if (Array.isArray(value)) value = value.join(", ");
        if (typeof value === "object") value = JSON.stringify(value);
        value = String(value);
        const needsQuotes = /[",\n\r;]/.test(value);
        const escaped = value.replace(/"/g, '""');
        return needsQuotes ? `"${escaped}"` : escaped;
      };

      const getCellRawValue = (row, key) => {
        if (key in row) return row[key];
        if (classificationKeys.includes(key)) {
          return row.classification ? row.classification[key] : null;
        }
        return null;
      };

      const formatValue = (field, row) => {
        let value = getCellRawValue(row, field.key);
        if (field.formatter) {
          try {
            return field.formatter(value, field.key, row);
          } catch (e) {
            // fallback below
          }
        }
        switch (field.key) {
          case "status":
            return this.getStatusText(row.status);
          case "time":
            return this.duration(row.time);
          case "createdAt":
          case "updatedAt":
            return this.formatDate(value);
          default:
            return value == null ? "" : value;
        }
      };

      const headers = this.activeFields.map((f) => f.label || f.key);
      const allRows = this.reportItems
        .filter((row) => this.filterResults(row, this.currentFilter))
        .map((row) => this.activeFields.map((f) => formatValue(f, row)));

      const separator = ";";
      const lines = [headers, ...allRows]
        .map((row) => row.map(escapeCsv).join(separator))
        .join("\r\n");

      const bom = "\ufeff"; // Excel-friendly BOM
      const blob = new Blob([bom + lines], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ts = moment().format("YYYYMMDD_HHmmss");
      a.href = url;
      a.download = `relatorio_ouvidoria_${ts}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },

    updateFields(checked) {
      const fields = [];
      const classificationFormatter = (value, key, item) => {
        if (!item.classification) {
          return "Sem Classificação";
        }
        value = item.classification[key];
        return this.classificationMaps[key][value] || "Sem Classificação";
      };
      for (const col of this.availableColumns) {
        if (checked && checked.indexOf(col.value) === -1) {
          continue;
        }
        const def = { key: col.value, label: col.text, sortable: true };
        if (col.classification) {
          def.formatter = classificationFormatter;
          def.sortByFormatted = true;
          def.sortable = false;
        } else if (col.formatter) {
          def.formatter = col.formatter;
          def.sortByFormatted = true;
        }
        fields.push(def);
      }

      this.fields = [
        { key: "protocol", label: "Protocolo", sortable: true },
        ...fields,
      ];
    },

    updateColumnsOptions() {
      const availableColumns = [
        {
          text: "Data de Recebimento",
          value: "createdAt",
          formatter: this.formatDate,
        },
        {
          text: "Situação",
          value: "status",
        },
        {
          text: "Tempo",
          value: "time",
        },
      ];

      const classification = [
        {
          value: "receivingMeans",
          text: "Meio de Recebimento",
          classification: true,
        },
        { value: "origin", text: "Origem", classification: true },
        {
          value: "reportingAgent",
          text: "Agente Denunciante",
          classification: true,
        },
        {
          value: "level",
          text: "Classificação/Classe Interna",
          classification: true,
        },
        { value: "nature", text: "Natureza", classification: true },
        {
          value: "responsibleDepartment",
          text: "Setor Responsável",
          classification: true,
        },
        {
          value: "involvedDepartment",
          text: "Setor da Ocorrência",
          classification: true,
        },
        { value: "businessUnit", text: "Unidade", classification: true },
        {
          value: "businessGroup",
          text: "Empresa do Grupo",
          classification: true,
        },
        {
          value: "relatedProject",
          text: "Contrato/Projeto",
          classification: true,
        },
      ];

      for (const item of classification) {
        if (this.companyClassification[item.value]) {
          availableColumns.push(item);
        }
      }

      const defaultSelectedColumns = [];
      const fields = [];
      for (const col of availableColumns) {
        defaultSelectedColumns.push(col.value);
        const def = { key: col.value, label: col.text, sortable: true };
        if (col.classification) {
          def.formatter = (value, key, item) => {
            value = item.classification[key];
            return this.classificationMaps[key][value];
          };
          def.sortByFormatted = true;
        }
        fields.push(def);
      }

      this.availableColumns = [...availableColumns];
      this.selectedColumns = [...defaultSelectedColumns];

      this.updateFields();
    },
    toggleSection(section) {
      this.collapsedSections[section] = !this.collapsedSections[section];
    },

    async loadCompanies() {
      if (!this.profile) {
        return;
      }

      try {
        let companies = [];

        if (this.profile.role === "root") {
          // Root: buscar todas as empresas paginadas
          const response = await getCompanies({ size: 1000, page: 1 });
          if (response.data && response.data.docs) {
            companies = response.data.docs;
          } else if (Array.isArray(response.data)) {
            companies = response.data;
          } else if (response.data && response.data.docs) {
            companies = response.data.docs;
          }
        } else if (this.profile.role === "company-root") {
          // Company-root: a API já retorna própria + gerenciadas
          const response = await getCompanies();
          companies = Array.isArray(response.data)
            ? response.data
            : response.data.docs || [];
        }

        this.availableCompanies = companies;
        console.log(
          "OuvidoriaReport - Companies loaded:",
          this.availableCompanies
        );
      } catch (error) {
        console.error("Erro ao carregar empresas:", error);
      }
    },
  },
};
</script>
<style scoped>
.ouvidoria-report {
  height: 100vh;
  overflow: hidden;
}

.skeleton-overlay {
  position: relative;
}

.skeleton-table {
  border-spacing: 0;
}

.skeleton-table tbody tr {
  box-shadow: none;
}

.skeleton-table tbody td {
  background-color: #fff;
}

.ouvidoria-report .row {
  height: 100%;
  margin: 0;
  align-items: stretch;
}

.ouvidoria-report .col-3,
.ouvidoria-report .col-9 {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.ouvidoria-report .col-9 {
  padding-right: 15px;
}

.ouvidoria-report .col-3 {
  padding-left: 15px;
}
.filters-sidebar {
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: #f8f9fa;
  border: none;
  border-radius: 8px;
}
/* Harmonize the report card style with the parameters card */
.ouvidoria-report #report.card {
  border: none;
  border-radius: 8px;
}
.filters-sidebar >>> .card-header {
  background-color: transparent;
  border-bottom: 1px solid #e9ecef;
  font-weight: 600;
  color: #343a40;
  font-size: 1.1rem;
}
.filters-sidebar .card-body {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.filters-container {
  flex: 1;
  overflow-y: auto;
  padding-right: 10px;
}

.ouvidoria-report .col-9 .card {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.ouvidoria-report .col-9 .card-body {
  flex: 1;
  overflow-y: auto;
}

.filter-group {
  margin-bottom: 1.5rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #e9ecef;
}

.filter-group:last-child {
  border-bottom: none;
}

.filter-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  padding: 0;
  border-radius: 4px;
  transition: background-color 0.2s ease;
}

.filter-header:hover {
  background-color: transparent;
}

.filter-title {
  color: #495057;
  font-weight: 600;
  margin-bottom: 0.75rem;
  padding-bottom: 0;
  border-bottom: none;
  flex: 1;
  font-size: 0.9rem;
}

.collapse-btn {
  color: #6c757d;
  transition: color 0.2s ease, transform 0.2s ease;
  line-height: 1;
}

.collapse-btn:hover {
  color: #007bff;
  text-decoration: none;
}

.collapse-btn i {
  transition: transform 0.3s ease;
  font-size: 0.875rem;
}

.filter-header:hover .collapse-btn {
  color: #007bff;
}

/* Animação suave para o collapse */
.collapse {
  transition: all 0.3s ease;
}

.collapsing {
  transition: height 0.3s ease;
}

.form-control[multiple] {
  min-height: 80px;
}

/* Estilos para checkboxes e radio buttons */
.form-check {
  padding-left: 1.5rem;
  margin-bottom: 0.5rem;
}

.form-check-input {
  margin-left: -1.5rem;
}

.form-check-label {
  font-size: 0.875rem;
  line-height: 1.4;
  color: #495057;
  cursor: pointer;
  transition: color 0.2s ease;
}

.form-check-label:hover {
  color: #007bff;
}

.custom-checkbox .custom-control-input:checked ~ .custom-control-label::before {
  background-color: #007bff;
  border-color: #007bff;
}

.custom-radio .custom-control-input:checked ~ .custom-control-label::before {
  background-color: #007bff;
  border-color: #007bff;
}

/* Espaçamento entre grupos de checkboxes */
.filter-group .form-group:not(:last-child) {
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #f0f0f0;
}

/* Scroll suave para containers de checkboxes */
.filter-group {
  max-height: none;
}

.filter-group .form-check-input:focus {
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.mb-2 {
  margin-bottom: 0.5rem;
}

.mb-3 {
  margin-bottom: 1rem;
}

.h-100 {
  min-height: calc(100vh - 120px);
}

/* Customização dos scrollbars */
.filters-container::-webkit-scrollbar {
  width: 6px;
}

.filters-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.filters-container::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}

.filters-container::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.d-flex.flex-column.mt-3 .b-button {
  border-radius: 6px;
  padding: 0.6rem 1rem;
  font-weight: 500;
  transition: background-color 0.2s ease, transform 0.2s ease,
    box-shadow 0.2s ease;
}
.d-flex.flex-column.mt-3 .b-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}
.d-flex.flex-column.mt-3 .btn-secondary {
  background-color: #6c757d;
  border-color: #6c757d;
}
.d-flex.flex-column.mt-3 .btn-primary {
  background-color: #007bff;
  border-color: #007bff;
}
/* Responsividade */
@media (max-width: 1200px) {
  .ouvidoria-report {
    height: auto;
    min-height: 100vh;
  }

  .filters-sidebar {
    height: auto;
    min-height: 400px;
  }

  .filters-container {
    height: auto;
    max-height: 400px;
  }

  .ouvidoria-report .col-3,
  .ouvidoria-report .col-9 {
    height: auto;
  }
}

@media (max-width: 768px) {
  .ouvidoria-report .col-3 {
    flex: 0 0 100%;
    max-width: 100%;
    margin-bottom: 20px;
    height: auto;
    padding-left: 0;
  }

  .ouvidoria-report .col-9 {
    flex: 0 0 100%;
    max-width: 100%;
    height: auto;
    padding-right: 0;
  }

  .ouvidoria-report .row {
    height: auto;
    flex-direction: column;
  }
}
</style>
<style>
.modern-table {
  border-collapse: separate;
  border-spacing: 0;
  width: 100%;
}

.modern-table thead th {
  background-color: #eef1f4;
  border: none;
  color: #56606b;
  font-weight: 600;
  padding: 1rem;
  text-transform: uppercase;
  font-size: 0.8rem;
  letter-spacing: 0.5px;
}

.modern-table thead th:first-child {
  border-top-left-radius: 10px;
  border-bottom-left-radius: 10px;
}

.modern-table thead th:last-child {
  border-top-right-radius: 10px;
  border-bottom-right-radius: 10px;
}

.modern-table tbody tr {
  background-color: #fff;
}

.modern-table tbody tr:hover {
  background-color: #fbfcfe;
}

.modern-table tbody td {
  border-top: none;
  border-bottom: 1px solid #e6eaef;
  padding: 1rem;
  vertical-align: middle;
}

.modern-table tbody tr:hover td {
  background-color: #fbfcfe;
}
.status-badge {
  padding: 0.4em 0.8em;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #212529;
  white-space: nowrap;
}
.status-new {
  background-color: #fce4e4;
  color: #c0392b;
}
.status-in-progress {
  background-color: #e0e7ff;
  color: #3730a3;
}
.status-concluded {
  background-color: #d1fae5;
  color: #065f46;
}
.status-default {
  background-color: #e0e7ff;
  color: #3730a3;
}
</style>
